import { pysharedAlertsConfig } from "pyshared";
import { errorsCatalog } from "shared-logic-core";
import { z } from "zod";

export type LegacyAlertCode = keyof typeof pysharedAlertsConfig;
export const LegacyAlertCodeSchema = z.enum(
  Object.keys(pysharedAlertsConfig) as [LegacyAlertCode, ...LegacyAlertCode[]],
);

const PREDICTION_ALERT_CODES = [
  "PREDICTION_101",
  "PREDICTION_102",
  "PREDICTION_103",
  "PREDICTION_104",
  "PREDICTION_105",
  "PREDICTION_106",
  "PREDICTION_107",
  "PREDICTION_109",
] as const satisfies readonly (keyof typeof errorsCatalog)[];

export type PredictionAlertCode = (typeof PREDICTION_ALERT_CODES)[number];
export const PredictionAlertCodeSchema = z.enum(PREDICTION_ALERT_CODES);

const NESTING_ERROR_CODES = [
  "NESTING_101",
  "NESTING_102",
  "NESTING_103",
  "NESTING_104",
  "NESTING_105",
  "NESTING_106",
] as const satisfies readonly (keyof typeof errorsCatalog)[];

export type NestingErrorCode = (typeof NESTING_ERROR_CODES)[number];
export const NestingErrorCodeSchema = z.enum(NESTING_ERROR_CODES);

export type AlertCode = LegacyAlertCode | PredictionAlertCode;
export const AlertCodeSchema = z.union([LegacyAlertCodeSchema, PredictionAlertCodeSchema]);

export const AlertSeveritySchema = z.union([
  z.literal("info"),
  z.literal("warning"),
  z.literal("error"),
]);
export type AlertSeverity = z.infer<typeof AlertSeveritySchema>;

export const AlertConfigSchema = z.object({
  description: z.string(),
  severity: AlertSeveritySchema,
  action: z.string().optional(),
  deprecated: z.boolean().optional(),
});
export type AlertConfig = z.infer<typeof AlertConfigSchema>;

const legacyAlertCodesConfig: Record<LegacyAlertCode, AlertConfig> = pysharedAlertsConfig;

const predictionAlertCodesConfig = Object.fromEntries(
  PREDICTION_ALERT_CODES.map((code) => {
    const { title, severity, action } = errorsCatalog[code];
    return [code, { description: title, severity, action }];
  }),
) as Record<PredictionAlertCode, AlertConfig>;

export const alertCodesConfig: Record<AlertCode, AlertConfig> = {
  ...legacyAlertCodesConfig,
  ...predictionAlertCodesConfig,
};

export const isLegacyAlertCode = (code: string): code is LegacyAlertCode => {
  return code in legacyAlertCodesConfig;
};

export const isAlertCode = (code: string): code is AlertCode => {
  return code in alertCodesConfig;
};

export const getAlertConfig = (code: string): AlertConfig | undefined => {
  if (isAlertCode(code)) {
    return alertCodesConfig[code];
  }
  return undefined;
};
